import React, { useState, useEffect } from 'react';
import { X, Download, Info } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [pwaStatus, setPwaStatus] = useState({
    hasManifest: false,
    hasServiceWorker: false,
    isHttps: false,
    hasEngagement: false
  });

  useEffect(() => {
    // Check PWA requirements
    const checkPWAStatus = () => {
      const status = {
        hasManifest: !!document.querySelector('link[rel="manifest"]'),
        hasServiceWorker: 'serviceWorker' in navigator,
        isHttps: window.location.protocol === 'https:',
        hasEngagement: false // Will be set based on user interaction
      };
      
      // Check if user has interacted with the site
      const hasInteracted = sessionStorage.getItem('user-interacted');
      if (hasInteracted) {
        status.hasEngagement = true;
      }
      
      setPwaStatus(status);
      console.log('PWA Status:', status);
    };

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Track user interaction for engagement criteria
    const trackInteraction = () => {
      sessionStorage.setItem('user-interacted', 'true');
      setPwaStatus(prev => ({ ...prev, hasEngagement: true }));
    };

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('beforeinstallprompt event fired!');
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      console.log('App installed!');
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    };



    // Add event listeners for user interaction
    document.addEventListener('click', trackInteraction, { once: true });
    document.addEventListener('scroll', trackInteraction, { once: true });

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check PWA status immediately and after a delay
    checkPWAStatus();
    const statusCheckTimer = setTimeout(checkPWAStatus, 2000);

    // Only show prompt once per session and only if user has engaged
    const hasShownThisSession = sessionStorage.getItem('pwa-prompt-shown');
    if (!hasShownThisSession) {
      const testTimer = setTimeout(() => {
        const hasInteracted = sessionStorage.getItem('user-interacted');
        if (!showInstallPrompt && !isInstalled && hasInteracted) {
          console.log('Showing PWA prompt after user engagement');
          setShowInstallPrompt(true);
          sessionStorage.setItem('pwa-prompt-shown', 'true');
        }
      }, 10000); // Wait 10 seconds and only if user has interacted

      return () => {
        document.removeEventListener('click', trackInteraction);
        document.removeEventListener('scroll', trackInteraction);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
        clearTimeout(testTimer);
        clearTimeout(statusCheckTimer);
      };
    }

    return () => {
      document.removeEventListener('click', trackInteraction);
      document.removeEventListener('scroll', trackInteraction);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearTimeout(statusCheckTimer);
    };
  }, [showInstallPrompt, isInstalled]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Show detailed status instead of alert
      console.log('No deferred prompt available. PWA Status:', pwaStatus);
      setShowInstallPrompt(false);
      return;
    }

    console.log('Triggering install prompt...');
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }
    
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    // Store dismissal in localStorage to avoid showing again for a while
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  const handleShowStatus = () => {
    const statusText = `
PWA Install Status:
✅ HTTPS: ${pwaStatus.isHttps ? 'Yes' : 'No'}
✅ Manifest: ${pwaStatus.hasManifest ? 'Yes' : 'No'}
✅ Service Worker: ${pwaStatus.hasServiceWorker ? 'Yes' : 'No'}
⚠️ User Engagement: ${pwaStatus.hasEngagement ? 'Yes' : 'No'}
⚠️ Deferred Prompt: ${deferredPrompt ? 'Available' : 'Not Available'}

The browser's beforeinstallprompt event hasn't fired yet. This usually means:
1. User hasn't interacted enough with the site
2. Browser's engagement criteria not met
3. App might already be installed

Try interacting more with the site (click, scroll, navigate) and then refresh the page.
    `;
    alert(statusText);
  };

  // Check if we should show the prompt (not dismissed recently)
  useEffect(() => {
    const dismissedTime = localStorage.getItem('pwa-install-dismissed');
    if (dismissedTime) {
      const timeSinceDismissed = Date.now() - parseInt(dismissedTime);
      const oneDay = 24 * 60 * 60 * 1000; // 24 hours
      if (timeSinceDismissed < oneDay) {
        setShowInstallPrompt(false);
      }
    }
  }, []);

  if (isInstalled || !showInstallPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[100] animate-in slide-in-from-bottom-4 duration-300">
      <div
        className="mx-auto max-w-sm rounded-2xl border border-black/10 p-4 shadow-2xl"
        style={{
          background: '#ffffff',
          color: '#0b0c10',
          boxShadow: '0 18px 50px -12px rgba(0,0,0,0.45)',
        }}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div
              className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl border border-black/10"
              style={{ background: '#0b0c10' }}
            >
              <img
                src="/brand/trendsetter-logo.png"
                alt="TrendSetter"
                className="h-8 w-8 object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="mb-1 text-sm font-semibold" style={{ color: '#0b0c10' }}>
              Install TrendSetter
            </h3>
            <p className="mb-3 text-xs" style={{ color: '#4b5563' }}>
              {deferredPrompt
                ? 'Install the app for quicker access, a smoother checkout, and easy re-visits.'
                : 'Install the app for faster access next time.'}
            </p>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleInstallClick}
                className="flex flex-1 items-center justify-center gap-1 rounded-lg px-3 py-2 text-xs font-semibold transition-opacity hover:opacity-90"
                style={{ background: '#f0812f', color: '#0b0c10' }}
              >
                <Download className="h-3 w-3" />
                {deferredPrompt ? 'Install' : 'Try Install'}
              </button>
              <button
                type="button"
                onClick={handleShowStatus}
                className="rounded-lg p-2 transition-colors hover:bg-black/5"
                style={{ color: '#6b7280' }}
                title="Show PWA Status"
              >
                <Info className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="rounded-lg p-2 transition-colors hover:bg-black/5"
                style={{ color: '#6b7280' }}
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt; 